import React, { useState, useCallback } from 'react';
import { 
  ChevronLeft, Play, Copy, Download, Settings2, 
  Grid3X3, ChevronDown, ChevronUp, Wrench
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  controllerConfig, 
  materialPresets, 
  millingOperations,
  generateMillingCode,
  formatGCodeLine 
} from '@/utils/gcode';
import { machinePresets } from '@/utils/turnmill';
import ProgramBuilder from '@/components/ProgramBuilder';

const MillingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('single');
  const [controller, setController] = useState('fanuc');
  const [machineType, setMachineType] = useState('3axis');
  const [machinePreset, setMachinePreset] = useState('hyundai-5600hd');
  const [selectedOp, setSelectedOp] = useState('face-mill');
  const [setupExpanded, setSetupExpanded] = useState(false);
  const [gcode, setGcode] = useState([]);
  const [copied, setCopied] = useState(false);

  // Workpiece setup state (inches)
  const [setup, setSetup] = useState({
    lengthX: 4.0,
    widthY: 3.0,
    heightZ: 1.0,
    material: 'alu-6061',
    origin: 'top-center',
    safeX: 6.0,
    safeY: 6.0,
    safeZ: 2.0,
  });

  // Operation parameters
  const [opParams, setOpParams] = useState({});
  
  // Program settings (inches)
  const [settings, setSettings] = useState({
    programNum: 1,
    programName: '',
    tool: 1,
    speed: 2000,
    feed: 12.0,
    addHeader: true,
    safeZ: 2.0,
  });

  // Update operation params when operation changes
  React.useEffect(() => {
    const op = millingOperations[selectedOp];
    if (op) {
      const defaults = {};
      op.fields.forEach(field => {
        defaults[field.id] = field.default;
      });
      setOpParams(defaults);
    }
  }, [selectedOp]);

  const handleParamChange = (id, value) => {
    setOpParams(prev => ({ ...prev, [id]: parseFloat(value) || 0 }));
  };

  const handleSettingChange = (id, value) => {
    setSettings(prev => ({ ...prev, [id]: value }));
  };

  const generateCode = useCallback(() => {
    const code = generateMillingCode(selectedOp, opParams, settings, controller);
    setGcode(code);
  }, [selectedOp, opParams, settings, controller]);

  const copyCode = async () => {
    const text = gcode.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for clipboard permission denied
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.error('Copy failed:', e);
      }
      document.body.removeChild(textArea);
    }
  };

  const downloadCode = () => {
    const text = gcode.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `O${String(settings.programNum).padStart(4, '0')}_${settings.programName || 'milling'}.nc`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: 'single', label: 'Single Op' },
    { id: 'builder', label: 'Program Builder' },
    { id: 'tools', label: 'Tools Guide' },
  ];

  const opCategories = {
    drilling: ['drill', 'peck-drill', 'tap'],
    milling2d: ['face-mill', 'pocket-rect', 'pocket-circ', 'contour', 'slot'],
    advanced: ['helical-bore'],
  };

  return (
    <div className="min-h-screen bg-industrial-bg">
      {/* Sub Header */}
      <div className="sticky top-14 z-20 bg-industrial-surface border-b border-industrial-border">
        <div className="flex items-center justify-between px-4 py-2">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-industrial-text-secondary hover:text-industrial-text transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          
          <h2 className="font-heading font-bold text-violet-400 flex items-center gap-2">
            <Grid3X3 className="w-5 h-5" />
            CNC MILLING
          </h2>

          <select
            value={controller}
            onChange={(e) => setController(e.target.value)}
            className="bg-industrial-surface-highlight border border-industrial-border rounded px-2 py-1 text-sm text-industrial-text"
            data-testid="controller-select"
          >
            {Object.entries(controllerConfig).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.name}</option>
            ))}
          </select>
        </div>

        {/* Machine Type Row */}
        <div className="flex items-center justify-center gap-2 px-4 py-2 border-t border-industrial-border bg-industrial-surface-highlight">
          <button
            onClick={() => setMachinePreset('hyundai-5600hd')}
            data-testid="preset-hyundai-5600hd"
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              machinePreset === 'hyundai-5600hd'
                ? 'bg-violet-500 text-white'
                : 'bg-industrial-surface border border-industrial-border text-industrial-text-secondary hover:border-violet-400'
            }`}
          >
            Hyundai 5600HD
          </button>
          {['3axis', '4axis', '5axis'].map(type => (
            <button
              key={type}
              onClick={() => setMachineType(type)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                machineType === type
                  ? 'bg-violet-500 text-white'
                  : 'bg-industrial-surface border border-industrial-border text-industrial-text-secondary hover:border-violet-400'
              }`}
            >
              {type === '3axis' ? '3-Axis' : type === '4axis' ? '4th Axis' : '5-Axis'}
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex border-t border-industrial-border">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-violet-400 border-b-2 border-violet-400 bg-violet-400/10'
                  : 'text-industrial-text-secondary hover:text-industrial-text'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Workpiece Setup Panel */}
      <div className="mx-4 mt-4">
        <button
          onClick={() => setSetupExpanded(!setupExpanded)}
          className="w-full flex items-center justify-between p-4 bg-industrial-surface border border-industrial-border rounded-t-lg"
          data-testid="setup-toggle"
        >
          <span className="flex items-center gap-2 font-medium text-industrial-readout">
            <Settings2 className="w-4 h-4" />
            Workpiece & Material Setup
          </span>
          {setupExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {setupExpanded && (
          <div className="bg-industrial-surface border-x border-b border-industrial-border rounded-b-lg p-4 space-y-4 animate-in">
            {/* Workpiece Dimensions */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Length X (in)</label>
                <input
                  type="number"
                  step="0.001"
                  value={setup.lengthX}
                  onChange={(e) => setSetup(s => ({ ...s, lengthX: parseFloat(e.target.value) }))}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Width Y (in)</label>
                <input
                  type="number"
                  step="0.001"
                  value={setup.widthY}
                  onChange={(e) => setSetup(s => ({ ...s, widthY: parseFloat(e.target.value) }))}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Height Z (in)</label>
                <input
                  type="number"
                  step="0.001"
                  value={setup.heightZ}
                  onChange={(e) => setSetup(s => ({ ...s, heightZ: parseFloat(e.target.value) }))}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
            </div>

            {/* Origin */}
            <div>
              <label className="text-xs text-industrial-text-secondary mb-1 block">Origin Location</label>
              <select
                value={setup.origin}
                onChange={(e) => setSetup(s => ({ ...s, origin: e.target.value }))}
                className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text"
              >
                <option value="top-center">Top Surface - Center</option>
                <option value="top-left">Top Surface - Left Front Corner</option>
                <option value="top-right">Top Surface - Right Front Corner</option>
                <option value="bottom-center">Bottom Surface - Center</option>
              </select>
            </div>

            {/* Material */}
            <div>
              <label className="text-xs text-industrial-text-secondary mb-1 block">Material</label>
              <select
                value={setup.material}
                onChange={(e) => setSetup(s => ({ ...s, material: e.target.value }))}
                className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text"
              >
                {Object.entries(materialPresets).map(([key, mat]) => (
                  <option key={key} value={key}>{mat.name}</option>
                ))}
              </select>
            </div>

            {/* Safe Distances */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Safe X (in)</label>
                <input
                  type="number"
                  step="0.001"
                  value={setup.safeX}
                  onChange={(e) => setSetup(s => ({ ...s, safeX: parseFloat(e.target.value) }))}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Safe Y (in)</label>
                <input
                  type="number"
                  step="0.001"
                  value={setup.safeY}
                  onChange={(e) => setSetup(s => ({ ...s, safeY: parseFloat(e.target.value) }))}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
              <div>
                <label className="text-xs text-industrial-text-secondary mb-1 block">Safe Z (in)</label>
                <input
                  type="number"
                  step="0.001"
                  value={setup.safeZ}
                  onChange={(e) => setSetup(s => ({ ...s, safeZ: parseFloat(e.target.value) }))}
                  className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="p-4">
        {activeTab === 'single' && (
          <div className="space-y-4 animate-in">
            {/* Drilling Operations */}
            <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
              <h3 className="text-xs uppercase tracking-wider text-industrial-text-secondary mb-3">Drilling</h3>
              <div className="grid grid-cols-3 gap-2">
                {opCategories.drilling.map(key => {
                  const op = millingOperations[key];
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedOp(key)}
                      data-testid={`op-${key}`}
                      className={`p-3 rounded-lg border text-center transition-all text-xs ${
                        selectedOp === key
                          ? 'bg-violet-500/20 border-violet-400 text-violet-400'
                          : 'bg-industrial-surface-highlight border-industrial-border text-industrial-text hover:border-violet-400'
                      }`}
                    >
                      {op.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 2D Milling Operations */}
            <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
              <h3 className="text-xs uppercase tracking-wider text-industrial-text-secondary mb-3">2D Milling</h3>
              <div className="grid grid-cols-3 gap-2">
                {opCategories.milling2d.map(key => {
                  const op = millingOperations[key];
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedOp(key)}
                      data-testid={`op-${key}`}
                      className={`p-3 rounded-lg border text-center transition-all text-xs ${
                        selectedOp === key
                          ? 'bg-violet-500/20 border-violet-400 text-violet-400'
                          : 'bg-industrial-surface-highlight border-industrial-border text-industrial-text hover:border-violet-400'
                      }`}
                    >
                      {op.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Operation Parameters */}
            {millingOperations[selectedOp] && (
              <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
                <h3 className="font-heading font-semibold text-industrial-text mb-1">
                  {millingOperations[selectedOp].name} Parameters
                </h3>
                <p className="text-xs text-industrial-text-secondary mb-4">
                  {millingOperations[selectedOp].description}
                </p>
                
                <div className="grid grid-cols-2 gap-3">
                  {millingOperations[selectedOp].fields.map(field => (
                    <div key={field.id}>
                      <label className="text-xs text-industrial-text-secondary mb-1 block">
                        {field.label}
                      </label>
                      <input
                        type="number"
                        value={opParams[field.id] ?? field.default}
                        onChange={(e) => handleParamChange(field.id, e.target.value)}
                        className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                        data-testid={`param-${field.id}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cutting Parameters */}
            <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
              <h3 className="font-heading font-semibold text-industrial-text mb-3">Cutting Parameters</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Tool #</label>
                  <input
                    type="number"
                    value={settings.tool}
                    onChange={(e) => handleSettingChange('tool', parseInt(e.target.value))}
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Speed (RPM)</label>
                  <input
                    type="number"
                    value={settings.speed}
                    onChange={(e) => handleSettingChange('speed', parseInt(e.target.value))}
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-industrial-text-secondary mb-1 block">Feed Rate (IPM)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={settings.feed}
                    onChange={(e) => handleSettingChange('feed', parseFloat(e.target.value))}
                    className="w-full bg-industrial-bg border border-industrial-border rounded px-3 py-2 text-industrial-text font-mono"
                  />
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={generateCode}
              data-testid="generate-btn"
              className="w-full py-4 bg-violet-500 text-white font-heading font-bold uppercase tracking-wider rounded-lg shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              Generate G-Code
            </button>

            {/* G-Code Output */}
            {gcode.length > 0 && (
              <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 bg-industrial-surface-highlight border-b border-industrial-border">
                  <span className="text-sm font-medium text-industrial-text">Generated G-Code</span>
                  <div className="flex gap-2">
                    <button
                      onClick={copyCode}
                      data-testid="copy-btn"
                      className="p-2 rounded hover:bg-industrial-border transition-colors"
                    >
                      <Copy className={`w-4 h-4 ${copied ? 'text-industrial-success' : 'text-industrial-text-secondary'}`} />
                    </button>
                    <button
                      onClick={downloadCode}
                      data-testid="download-btn"
                      className="p-2 rounded hover:bg-industrial-border transition-colors"
                    >
                      <Download className="w-4 h-4 text-industrial-text-secondary" />
                    </button>
                  </div>
                </div>
                <div className="p-4 max-h-80 overflow-y-auto bg-[#1e1e1e]">
                  <pre className="font-mono text-sm leading-relaxed">
                    {gcode.map((line, i) => (
                      <div 
                        key={i} 
                        className="flex"
                        dangerouslySetInnerHTML={{ 
                          __html: `<span class="gcode-line-number w-8 inline-block">${String(i + 1).padStart(3)}</span>${formatGCodeLine(line)}`
                        }}
                      />
                    ))}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'builder' && (
          <ProgramBuilder 
            type="milling" 
            controller={controller}
          />
        )}

        {activeTab === 'tools' && (
          <div className="space-y-4 animate-in">
            <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
              <h3 className="font-heading font-semibold text-industrial-text mb-4">Milling Operations Reference</h3>
              
              {Object.entries(millingOperations).map(([key, op]) => (
                <div key={key} className="border-b border-industrial-border last:border-0 py-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-industrial-text">{op.name}</span>
                  </div>
                  <p className="text-sm text-industrial-text-secondary">{op.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
              <h3 className="font-heading font-semibold text-industrial-text mb-4">Canned Cycle Reference</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-industrial-border">
                      <th className="text-left py-2 text-industrial-text-secondary">Cycle</th>
                      <th className="text-left py-2 text-violet-400">FANUC</th>
                      <th className="text-left py-2 text-violet-400">Siemens</th>
                    </tr>
                  </thead>
                  <tbody className="text-industrial-text">
                    <tr className="border-b border-industrial-border">
                      <td className="py-2">Drill</td>
                      <td>G81</td>
                      <td>CYCLE81</td>
                    </tr>
                    <tr className="border-b border-industrial-border">
                      <td className="py-2">Peck Drill</td>
                      <td>G83</td>
                      <td>CYCLE83</td>
                    </tr>
                    <tr className="border-b border-industrial-border">
                      <td className="py-2">Tapping</td>
                      <td>G84</td>
                      <td>CYCLE84</td>
                    </tr>
                    <tr>
                      <td className="py-2">Boring</td>
                      <td>G85/G86</td>
                      <td>CYCLE85/86</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MillingPage;
